import {
  applicantSection,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { PRD, REG_1709, VEHICLE_ORGAN, coOwnersFields, vehicleFields } from "modules/ebiForms/definitions/sharedVehiclesConstruction";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * F2. Zawiadomienie o zbyciu albo nabyciu pojazdu — art. 78 ust. 2–2b ustawy Prawo o ruchu drogowym, § 14–15
 * rozporządzenia MI z 8.11.2024 r. (Dz. U. poz. 1709). Wzór ogólnopolski — załącznik nr 1 do rozporządzenia (pole
 * „zawiadamiam o zbyciu”, pkt 1–6 danych pojazdu, data zbycia), https://api.sejm.gov.pl/eli/acts/DU/2024/1709/text.pdf —
 * pobrany 2026-10-22; dane nabywcy i oświadczenie o działaniu za zgodą większości współwłaścicieli jak wzory Starostwa
 * w Policach (https://powiatpolicki-docrepo-api.bip.net.pl/api/files/2057) i Powiatu Nowodworskiego — pobrane.
 * Nabycie po 1.01.2024 r. nie wymaga zawiadomienia (art. 73aa — rejestracja F1); wariant „nabycie” dotyczy pojazdów nabytych
 * wcześniej i dotąd niezgłoszonych (dawny art. 78 ust. 2 pkt 1). PESEL/REGON nabywcy nieobowiązkowy — przepis go nie
 * wymaga, a dane nabywcy zawiera dołączona umowa (§ 15 ust. 1).
 */
const SALE: EbiCondition = { field: "rodzajZawiadomienia", equals: "zbycie" };
const PURCHASE: EbiCondition = { field: "rodzajZawiadomienia", equals: "nabycie" };

const F2: IEbiFormDefinition = {
  code: "F2",
  version: 1,
  title: "Zawiadomienie o zbyciu lub nabyciu pojazdu",
  category: EEbiFormCategory.Vehicles,
  office: EEbiFormOffice.County,
  documentType: "Pismo",
  description: "Zgłoś sprzedaż, darowiznę lub inne zbycie zarejestrowanego pojazdu (albo niezgłoszone nabycie sprzed 2024 r.).",
  keywords: ["zbycie", "sprzedaż", "nabycie", "kupno", "darowizna", "pojazd", "samochód", "zawiadomienie"],
  organ: `${VEHICLE_ORGAN} (zawiadomienie można złożyć do dowolnego starosty — art. 78 ust. 2b)`,
  legalBasis: `art. 78 ust. 2–2b ${PRD}; § 15 ${REG_1709}`,
  request: "Na podstawie art. 78 ust. 2 ustawy — Prawo o ruchu drogowym zawiadamiam o zbyciu (nabyciu) pojazdu opisanego w zawiadomieniu.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty; pełnomocnictwo — 17 zł.",
  variantField: "rodzajZawiadomienia",
  variants: {
    zbycie: { title: "Zawiadomienie o zbyciu pojazdu", documentType: "Pismo" },
    nabycie: { title: "Zawiadomienie o nabyciu pojazdu", documentType: "Pismo" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj zawiadomienia",
      fields: [
        {
          code: "rodzajZawiadomienia",
          type: "radio",
          label: "Zawiadamiam o",
          required: true,
          defaultValue: "zbycie",
          options: [
            { value: "zbycie", label: "zbyciu pojazdu (sprzedaż, darowizna, zamiana)" },
            { value: "nabycie", label: "nabyciu pojazdu przed 1 stycznia 2024 r., którego nabycia dotąd nie zgłoszono" },
          ],
        },
        {
          ...feeInfo(
            "infoZbycie",
            "Zawiadomienie o zbyciu pojazdu zarejestrowanego składa się w terminie 30 dni od dnia zbycia (art. 78 ust. 2 Prawa o ruchu drogowym); za niedopełnienie obowiązku grozi kara pieniężna (art. 140mb). Jeśli pojazd miał współwłaścicieli, zawiadomienie może złożyć jeden z nich (art. 78 ust. 2a).",
          ),
          visibleWhen: SALE,
        },
        {
          ...feeInfo(
            "infoNabycie",
            "Pojazdu nabytego po 1 stycznia 2024 r. nie zgłasza się — w ciągu 30 dni od nabycia złóż wniosek o rejestrację pojazdu (osobny wniosek w katalogu; przedsiębiorca prowadzący działalność w zakresie obrotu pojazdami — 90 dni). Zawiadomienie o nabyciu dotyczy tylko pojazdu nabytego wcześniej.",
          ),
          visibleWhen: PURCHASE,
        },
      ],
    },
    applicantSection("Zgłaszający"),
    representationSection(),
    {
      code: "pojazd",
      title: "Dane pojazdu",
      fields: [...vehicleFields({ full: true }), ...coOwnersFields("Czy pojazd był przedmiotem współwłasności?", SALE)],
    },
    {
      code: "zbycie",
      title: "Zbycie pojazdu",
      visibleWhen: SALE,
      fields: [
        { code: "dataZbycia", type: "date", label: "Data zbycia pojazdu", required: true, width: "half" },
        {
          code: "sposobZbycia",
          type: "radio",
          label: "Sposób zbycia",
          required: true,
          options: [
            { value: "sprzedaz", label: "sprzedaż" },
            { value: "darowizna", label: "darowizna" },
            { value: "inne", label: "inna umowa (np. zamiana)" },
          ],
        },
        { code: "nabywcaNazwa", type: "text", label: "Imię i nazwisko albo nazwa nabywcy", required: true, maxLength: 300 },
        { code: "nabywcaAdres", type: "text", label: "Adres zamieszkania albo siedziba nabywcy", required: true, maxLength: 300 },
        {
          code: "nabywcaIdentyfikator",
          type: "text",
          label: "Numer PESEL albo REGON nabywcy (nieobowiązkowo)",
          width: "half",
          maxLength: 14,
          hint: "Jeśli jest znany — ułatwia urzędowi powiązanie pojazdu z nabywcą.",
        },
        { code: "dokumentyPrzekazane", type: "checkbox", label: "Dowód rejestracyjny i tablice rejestracyjne przekazano nabywcy" },
      ],
    },
    {
      code: "nabycie",
      title: "Nabycie pojazdu",
      visibleWhen: PURCHASE,
      fields: [
        { code: "dataNabycia", type: "date", label: "Data nabycia pojazdu", required: true, width: "half" },
        { code: "zbywcaNazwa", type: "text", label: "Imię i nazwisko albo nazwa zbywcy (nieobowiązkowo)", maxLength: 300 },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "dokumentZbycia",
      label: "Kopia dokumentu, na podstawie którego nastąpiło zbycie pojazdu",
      hint: "Umowa sprzedaży, darowizny lub zamiany albo faktura (§ 15 ust. 1 rozporządzenia).",
      visibleWhen: SALE,
      required: true,
    },
    {
      code: "dokumentNabycia",
      label: "Kopia dokumentu potwierdzającego nabycie pojazdu",
      hint: "Umowa albo faktura.",
      visibleWhen: PURCHASE,
      required: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default F2;
